import { useEffect, useState, useRef, useCallback, Fragment } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Upload, RefreshCw, Settings2, Save, Wallet, Lock, Unlock, ShieldAlert, Pencil, X, Search, Eye, EyeOff, Plus,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const CARRIERS = ['Unlocked', 'Locked', 'ATT Locked', 'iCloud Locked'];
const WORKING_GRADES = ['Sealed', 'Opened', 'A', 'B/B+', 'B-', 'C', 'D', 'DOA'];
const PARTS_IC_GRADES = ['A/B', 'C', 'D', 'DOA'];
const PRICE_SECTIONS = {
  used: { label: 'Used iPhones', grades: WORKING_GRADES },
  new_iphones: { label: 'New iPhones', grades: ['Sealed', 'Opened'] },
  new_ipads: { label: 'New iPads', grades: ['Sealed', 'Opened'] },
  used_ipads: { label: 'Used iPads', grades: ['B', 'C', 'D'] },
  apple_watch: { label: 'Apple Watch', grades: ['Sealed', 'Opened', 'A', 'B'] },
};
const marginLabel = cell => cell.margin_type === 'percentage' ? `${Number((cell.margin * 100).toFixed(2))}%` : `$${Number(cell.margin || 0).toLocaleString()}`;

const CARRIER_ICON = {
  Unlocked: Unlock,
  Locked: Lock,
  'ATT Locked': Lock,
  'iCloud Locked': ShieldAlert,
};

const CARRIER_COLOR = {
  Unlocked: 'text-emerald-600',
  Locked: 'text-blue-600',
  'ATT Locked': 'text-cyan-600',
  'iCloud Locked': 'text-amber-600',
};

const CARRIER_BG = {
  Unlocked: 'bg-emerald-50 dark:bg-emerald-950/30',
  Locked: 'bg-blue-50 dark:bg-blue-950/30',
  'ATT Locked': 'bg-cyan-50 dark:bg-cyan-950/30',
  'iCloud Locked': 'bg-amber-50 dark:bg-amber-950/30',
};

const MarginEditor = ({ testId, value, onChange, mode, onMode, allowPercentage, onSave, onCancel }) => (
  <div className="flex flex-wrap items-center justify-end gap-1"
    onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) onSave(); }}
    onKeyDown={e => {
      if (e.key === 'Enter') { e.preventDefault(); onSave(); }
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
    }}>
    {allowPercentage ? <select aria-label="Profit type" data-testid={`${testId}-type`} value={mode}
      onChange={e => onMode(e.target.value)} className="h-7 w-10 rounded border border-input bg-background text-xs">
      <option value="fixed">$</option><option value="percentage">%</option>
    </select> : <span className="text-xs">$</span>}
    <Input type="number" min="0" max={mode === 'percentage' ? 100 : undefined} step="0.01" value={value}
      onChange={e => onChange(e.target.value)} autoFocus className="h-7 w-14 text-right px-1 text-xs" data-testid={testId} />
    <Button size="sm" variant="ghost" className="h-7 px-1 text-xs" data-testid={`${testId}-save`}
      onMouseDown={e => e.preventDefault()} onClick={onSave}>Save</Button>
  </div>
);

export default function MyPrices() {
  const [rows, setRows] = useState([]);
  const [section, setSection] = useState('used');
  const requestVersion = useRef(0);
  const sectionGrades = PRICE_SECTIONS[section].grades;
  const chooseSection = value => {
    if (value === section) return;
    setSection(value);
    setWorkingGrade(value === 'used' ? 'B/B+' : PRICE_SECTIONS[value].grades[0]);
    setRows([]); setEditCell(null); setDrill(null); setSearch('');
  };
  const [totals, setTotals] = useState({ models: 0, margins: 0, price_entries: 0 });
  const [loading, setLoading] = useState(true);
  const [editCell, setEditCell] = useState(null); // {model, carrier, field}
  const [editValue, setEditValue] = useState('');
  const [editMode, setEditMode] = useState('fixed');
  const savingEdit = useRef(false);
  const [syncCfg, setSyncCfg] = useState({ sheet_url: '', sheet_url_parts_ic: '', last_synced_at: null, last_sync_result: null });
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [workingGrade, setWorkingGrade] = useState('B/B+');
  const [icloudGrade, setIcloudGrade] = useState('A/B');
  const [addPriceOpen, setAddPriceOpen] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);
  const emptyPrice = { model: '', storage: '256GB', carrier: 'Unlocked', grade: 'Sealed', price: '', profit_margin: '' };
  const [newPrice, setNewPrice] = useState(emptyPrice);
  const [importResult, setImportResult] = useState(null);
  const [drill, setDrill] = useState(null); // { model, carrier }
  const [search, setSearch] = useState('');
  const [hideAtt, setHideAtt] = useState(() => {
    try { return localStorage.getItem('fliptrack_hide_att') === '1'; } catch { return false; }
  });
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const [pp, sc] = await Promise.all([
        axios.get(`${API}/paying-prices`, { params: { section, _t: Date.now() } }),
        axios.get(`${API}/sync/config`, { params: { _t: Date.now() } }),
      ]);
      if (version !== requestVersion.current) return;
      setRows(pp.data.rows || []);
      setTotals(pp.data.totals || { models: 0, margins: 0, price_entries: 0 });
      setSyncCfg(sc.data || { sheet_url: '' });
    } catch (e) {
      toast.error('Failed to load paying prices');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [section]);

  useEffect(() => { load(); return () => { requestVersion.current += 1; }; }, [load]);

  // Restore lowest-capacity quotes at the deadline, including after tab sleep.
  useEffect(() => {
    const expire = () => {
      const now = Date.now();
      const expired = new Set(rows.filter(row => row.storage_expires_at && new Date(row.storage_expires_at).getTime() <= now).map(row => row.model));
      if (!expired.size) return;
      setRows(current => current.map(row => {
        if (!expired.has(row.model)) return row;
        return { ...row, storage: row.default_storage, storage_expires_at: null, carriers: row.default_carriers };
      }));
      setEditCell(cell => cell && expired.has(cell.model) ? null : cell);
    };
    const deadlines = rows.filter(r => r.storage_expires_at).map(r => new Date(r.storage_expires_at).getTime());
    const timer = deadlines.length ? setTimeout(expire, Math.max(0, Math.min(...deadlines) - Date.now()) + 25) : null;
    const onFocus = () => { expire(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { if (timer) clearTimeout(timer); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [rows]);

  const addPrice = async () => {
    if (!newPrice.model.trim() || !newPrice.storage.trim() || newPrice.price === '' || newPrice.profit_margin === '' || Number(newPrice.price) < 0 || Number(newPrice.profit_margin) < 0) {
      toast.error('Enter a model, storage, buyer price and non-negative profit margin');
      return;
    }
    setSavingPrice(true);
    try {
      await axios.post(`${API}/paying-prices/entry`, { ...newPrice, price: Number(newPrice.price), profit_margin: Number(newPrice.profit_margin) });
      setWorkingGrade(newPrice.grade);
      setSearch(newPrice.model.trim());
      setAddPriceOpen(false);
      setNewPrice(emptyPrice);
      await load();
      toast.success('Device price saved — available in Inventory and Calculator');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save device price');
    } finally { setSavingPrice(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(`${API}/margins/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { imported, updated, total_errors } = r.data;
      if (imported === 0 && updated === 0) {
        toast.error(
          `Couldn't read any rows from "${file.name}". Make sure it's an MM_PHONEZ CSV (Used iPhones or Parts_IC).`,
          { duration: 6000 },
        );
        setImportResult({ ...r.data, file: file.name });
      } else {
        toast.success(
          `Imported ${imported} new margins, updated ${updated}${total_errors ? `, ${total_errors} skipped rows` : ''}`,
        );
        if (total_errors > 0) setImportResult({ ...r.data, file: file.name });
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSyncSave = async () => {
    try {
      await axios.post(`${API}/sync/config`, {
        sheet_url: syncCfg.sheet_url || '',
        sheet_url_parts_ic: syncCfg.sheet_url_parts_ic || '',
      });
      toast.success('K3no sheet URLs saved');
    } catch (e) {
      toast.error('Failed to save URLs');
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/sync/run`);
      const imported = r.data?.imported || 0;
      const updated = r.data?.updated || 0;
      const errors = Array.isArray(r.data?.errors) ? r.data.errors : [];
      if (errors.length > 0) {
        // Show ONE toast per distinct error so the user can act on it
        errors.forEach((msg) => toast.error(msg, { duration: 12000 }));
        // If we still imported/updated something, tell them what did work
        if (imported || updated) {
          toast.message(`Partial sync · ${imported} new, ${updated} updated`);
        }
      } else if (imported === 0 && updated === 0) {
        toast.warning(
          'Sync ran, but 0 prices changed — your prices are already up to date.',
          { duration: 8000 },
        );
      } else {
        toast.success(`Synced from K3no · ${imported} new, ${updated} updated`);
      }
      await load();
      // Only close the dialog if it was a clean success
      if (errors.length === 0 && (imported || updated)) setSyncOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const setModelStorage = async (model, storage) => {
    try {
      await axios.post(`${API}/margins/set-storage`, { model, storage, section });
      await load();
      toast.success(`${model} now reads ${storage} prices`);
    } catch (e) {
      toast.error('Failed to change storage');
    }
  };

  const saveMargin = async (model, carrier, storage, profit_margin, manual_buyer_price, margin_type = 'fixed') => {
    try {
      await axios.post(`${API}/margins/entry`, {
        model, carrier, storage: storage || 'ANY',
        sheet_source: section,
        margin_type,
        profit_margin: (parseFloat(profit_margin) || 0) / (margin_type === 'percentage' ? 100 : 1),
        manual_buyer_price: manual_buyer_price === '' || manual_buyer_price == null
          ? null : parseFloat(manual_buyer_price),
      });
      await load();
      return true;
    } catch (e) {
      toast.error('Save failed');
      return false;
    }
  };

  const startEdit = (model, carrier, field, currentVal) => {
    const cell = rows.find(r => r.model === model)?.carriers?.[carrier];
    const mode = cell?.margin_type || (section === 'used' ? 'fixed' : 'percentage');
    setEditMode(mode);
    setEditCell({ model, carrier, field });
    setEditValue(currentVal == null ? '' : String(field === 'margin' && mode === 'percentage' ? Number((currentVal * 100).toFixed(2)) : currentVal));
  };

  const commitEdit = async () => {
    if (!editCell || savingEdit.current) return;
    const { model, carrier, field } = editCell;
    const row = rows.find(r => r.model === model);
    const cell = row?.carriers?.[carrier];
    const newVal = parseFloat(editValue);
    const mode = field === 'manual_buyer' ? (cell?.margin_type || 'fixed') : editMode;
    if (field !== 'manual_buyer' && (!Number.isFinite(newVal) || newVal < 0 || (mode === 'percentage' && newVal > 100))) {
      toast.error(mode === 'percentage' ? 'Enter a percentage from 0 to 100' : 'Enter the dollar profit you want');
      return;
    }
    savingEdit.current = true;
    try {
      if (field === 'newmargin') {
        const ok = await saveMargin(model, carrier, row?.storage || 'ANY', newVal, null, mode);
        if (ok) { toast.success(`${carrier} margin set for ${model}`); setEditCell(null); }
        return;
      }
      if (!cell) { setEditCell(null); return; }
      const profit = field === 'margin' ? newVal : cell.margin * (mode === 'percentage' ? 100 : 1);
      const manualBuyer = field === 'manual_buyer' ? (isNaN(newVal) ? null : newVal) : (cell.source === 'manual' ? cell.buyer_price : null);
      const ok = await saveMargin(model, carrier, cell.margin_storage || cell.storage, profit, manualBuyer, mode);
      if (ok) setEditCell(null);
    } finally { savingEdit.current = false; }
  };

  // Time-since helper
  const fmtSince = (iso) => {
    if (!iso) return 'never';
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return `${Math.round(diff)}s ago`;
      if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
      return d.toLocaleDateString();
    } catch { return iso; }
  };

  const lastSyncErrors = Array.isArray(syncCfg?.last_sync_result?.errors)
    ? syncCfg.last_sync_result.errors.filter(Boolean)
    : [];
  const hasSyncError = lastSyncErrors.length > 0;

  const q = search.trim().toLowerCase();
  const displayedCarriers = section === 'used' ? (hideAtt ? CARRIERS.filter(c => c !== 'ATT Locked') : CARRIERS)
    : CARRIERS.filter(c => rows.some(r => r.carriers?.[c]));
  const highestPay = row => Math.max(...displayedCarriers.map(carrier =>
    row.carriers?.[carrier]?.grade_breakdown?.[carrier === 'iCloud Locked' ? icloudGrade : workingGrade]?.paying_price ?? -Infinity));
  const filteredRows = rows.filter(r => !q || r.model.toLowerCase().includes(q))
    .sort((a, b) => highestPay(b) - highestPay(a) || a.model.localeCompare(b.model));
  const toggleHideAtt = () => {
    setHideAtt(prev => {
      const next = !prev;
      try { localStorage.setItem('fliptrack_hide_att', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="my-prices-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-foreground tracking-wide">MY PAYING PRICES</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {section === 'used' ? 'What you should pay = K3no Buyer Price − Your Profit Margin' : 'Choose $ profit or % margin · paying prices round DOWN to $10'} · syncs hourly
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {section === 'used' && <Button onClick={() => setAddPriceOpen(true)} data-testid="add-device-price-btn"><Plus className="h-4 w-4 mr-2" /> Add model / price</Button>}
          <input
            type="file" accept=".csv" ref={fileInputRef}
            className="hidden" onChange={handleImport} data-testid="import-margins-input"
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-margins-btn">
            <Upload className="h-4 w-4 mr-2" /> Import MM_PHONEZ
          </Button>
          <Button variant="outline" onClick={() => setSyncOpen(true)} data-testid="open-sync-btn">
            <Settings2 className="h-4 w-4 mr-2" /> K3no Auto-Sync
          </Button>
          <Button
            onClick={handleSyncNow}
            disabled={syncing || (!syncCfg.sheet_url && !syncCfg.sheet_url_parts_ic)}
            data-testid="sync-now-btn"
            className="relative"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync Now'}
            {hasSyncError && !syncing && (
              <span
                className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-background animate-pulse"
                title="Last sync had errors — click to retry"
                data-testid="sync-error-dot"
              />
            )}
          </Button>
        </div>
      </div>

      {/* Sync error banner */}
      {hasSyncError && (
        <Card
          className="border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 px-4 py-3"
          data-testid="sync-error-banner"
        >
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                Last K3no sync had {lastSyncErrors.length === 1 ? 'an error' : `${lastSyncErrors.length} errors`} — your prices may be stale.
              </p>
              <ul className="mt-1 space-y-1 text-xs text-red-700 dark:text-red-300">
                {lastSyncErrors.map((msg, i) => (
                  <li key={i} className="break-words">• {msg}</li>
                ))}
              </ul>
              <div className="mt-2 flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-red-300 hover:bg-red-100 dark:border-red-800 dark:hover:bg-red-950/50"
                  onClick={() => setSyncOpen(true)}
                  data-testid="sync-error-fix-btn"
                >
                  Update sheet URL
                </Button>
                <Button
                  size="sm"
                  className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleSyncNow}
                  disabled={syncing}
                  data-testid="sync-error-retry-btn"
                >
                  Retry sync
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-2" data-testid="price-section-tabs">
        {Object.entries(PRICE_SECTIONS).map(([value, option]) => (
          <Button key={value} variant={section === value ? 'default' : 'outline'} size="sm" onClick={() => chooseSection(value)}
            aria-pressed={section === value} data-testid={`price-section-${value}`}>{option.label}</Button>
        ))}
      </div>
      {/* Stat bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="px-4 py-3 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900">
          <p className="text-xs text-emerald-700 dark:text-emerald-400 font-semibold uppercase">Models tracked</p>
          <p className="text-2xl font-display text-emerald-900 dark:text-emerald-100 mt-1" data-testid="stat-models">
            {totals.models}
          </p>
        </Card>
        <Card className="px-4 py-3 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900">
          <p className="text-xs text-blue-700 dark:text-blue-400 font-semibold uppercase">Margin rules</p>
          <p className="text-2xl font-display text-blue-900 dark:text-blue-100 mt-1" data-testid="stat-margins">
            {totals.margins}
          </p>
        </Card>
        <Card className="px-4 py-3 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900">
          <p className="text-xs text-amber-700 dark:text-amber-400 font-semibold uppercase">Buyer prices</p>
          <p className="text-2xl font-display text-amber-900 dark:text-amber-100 mt-1" data-testid="stat-prices">
            {totals.price_entries}
            {totals.parts_ic_entries > 0 && (
              <span className="text-sm text-amber-700/70 ml-1">+ {totals.parts_ic_entries} IC</span>
            )}
          </p>
        </Card>
        <Card className={`px-4 py-3 ${
          hasSyncError
            ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-900'
            : 'bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-900'
        }`}>
          <p className={`text-xs font-semibold uppercase flex items-center gap-1 ${
            hasSyncError ? 'text-red-700 dark:text-red-400' : 'text-violet-700 dark:text-violet-400'
          }`}>
            {hasSyncError && <ShieldAlert className="h-3 w-3" />}
            Last K3no sync
          </p>
          <p className={`text-base font-display mt-1 ${
            hasSyncError ? 'text-red-900 dark:text-red-100' : 'text-violet-900 dark:text-violet-100'
          }`} data-testid="stat-last-sync">
            {fmtSince(syncCfg.last_synced_at)}
            {hasSyncError && <span className="text-xs font-sans font-normal ml-1">(with errors)</span>}
          </p>
        </Card>
      </div>

      {/* Search + view toggle */}
      {rows.length > 0 && (
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-2">
            <div className="relative w-full sm:w-80" data-testid="price-search-wrap">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search device model…"
                className="pl-9"
                data-testid="price-search-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="price-search-clear"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {search && (
              <p className="text-xs text-muted-foreground" data-testid="price-search-count">
                {filteredRows.length} of {rows.length} models
              </p>
            )}
          </div>
          {section === 'used' && <Button
            variant="outline"
            size="sm"
            onClick={toggleHideAtt}
            data-testid="toggle-att-btn"
          >
            {hideAtt
              ? <><Eye className="h-4 w-4 mr-1.5" /> Show ATT</>
              : <><EyeOff className="h-4 w-4 mr-1.5" /> Hide ATT</>}
          </Button>}
        </div>
      )}

      <p className="text-xs text-muted-foreground" data-testid="storage-expiry-notice">Prices default to each model’s lowest storage. Checking another size switches back automatically after 30 seconds. Saved inventory storage is never changed.</p>
      <p className="text-xs text-muted-foreground" data-testid="paying-price-sort-note">Highest paying first for the selected grade and storage. Profit = K3no buyer price − your pay.</p>
      {/* Main table */}
      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center">
          <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            {section === 'used' ? 'No profit margins yet. Import your MM_PHONEZ CSV to start calculating paying prices.' : `No ${PRICE_SECTIONS[section].label} prices yet. Sync K3no to load them with a 20% margin.`}
          </p>
          <Button onClick={() => section === 'used' ? fileInputRef.current?.click() : handleSyncNow()} disabled={syncing} data-testid="empty-import-btn">
            {section === 'used' ? <><Upload className="h-4 w-4 mr-2" /> Import MM_PHONEZ CSV</> : <><RefreshCw className="h-4 w-4 mr-2" /> Sync K3no prices</>}
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden border-2 border-foreground/10">
          <div className="bg-sheet text-white px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
            <h3 className="font-display tracking-wider text-lg">PAYING PRICES BY MODEL</h3>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1.5" data-testid="grade-tabs">
                <span className="text-xs uppercase tracking-wider text-white/70 mr-1">Working</span>
                {sectionGrades.map(g => (
                  <button
                    key={g}
                    onClick={() => setWorkingGrade(g)}
                    className={`px-2.5 py-1 rounded text-xs font-semibold transition-colors ${
                      workingGrade === g
                        ? 'bg-white text-sheet'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                    data-testid={`grade-tab-${g.replace(/[^A-Za-z0-9]/g, '')}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
              {section === 'used' && <div className="flex items-center gap-1.5" data-testid="ic-grade-tabs">
                <span className="text-xs uppercase tracking-wider text-amber-300 mr-1">iCloud</span>
                {PARTS_IC_GRADES.map(g => (
                  <button
                    key={g}
                    onClick={() => setIcloudGrade(g)}
                    className={`px-2.5 py-1 rounded text-xs font-semibold transition-colors ${
                      icloudGrade === g
                        ? 'bg-amber-300 text-sheet'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                    data-testid={`ic-grade-tab-${g.replace(/[^A-Za-z0-9]/g, '')}`}
                  >
                    {g}
                  </button>
                ))}
              </div>}
            </div>
          </div>
          <div className="overflow-x-auto hidden sm:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground sticky left-0 bg-muted/50 z-10">
                    Model
                  </th>
                  {displayedCarriers.map(c => {
                    const Icon = CARRIER_ICON[c];
                    return (
                      <th key={c} className={`text-center px-4 py-2.5 font-semibold ${CARRIER_COLOR[c]}`} colSpan={3}>
                        <div className="flex items-center justify-center gap-2">
                          <Icon className="h-4 w-4" />
                          {c}
                        </div>
                      </th>
                    );
                  })}
                </tr>
                <tr className="text-xs text-muted-foreground border-t border-border">
                  <th className="px-4 py-1.5 sticky left-0 bg-muted/50 z-10"></th>
                  {displayedCarriers.map(c => (
                    <Fragment key={`hdr-${c}`}>
                      <th className="px-2 py-1.5 text-right font-medium">K3no</th>
                      <th className="px-2 py-1.5 text-right font-medium">Margin</th>
                      <th className="px-2 py-1.5 text-right font-bold">Pay</th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => (
                  <tr key={row.model} className="border-t border-border hover:bg-muted/30" data-testid={`row-${row.model}`}>
                    <td className="px-4 py-2 font-medium text-foreground sticky left-0 bg-background z-10">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span>{row.model}</span>
                        {row.available_storages?.length > 0 && (
                          <Select
                            value={row.storage || ''}
                            onValueChange={(v) => setModelStorage(row.model, v)}
                          >
                            <SelectTrigger
                              className="h-6 w-[96px] text-[11px] px-2 py-0 font-normal text-muted-foreground shrink-0"
                              data-testid={`storage-select-${row.model}`}
                            >
                              <SelectValue placeholder="Storage?" />
                            </SelectTrigger>
                            <SelectContent>
                              {row.available_storages.map((s) => (
                                <SelectItem key={s} value={s} className="text-xs" data-testid={`storage-opt-${row.model}-${s}`}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </td>
                    {displayedCarriers.map(carrier => {
                      const cell = row.carriers?.[carrier];
                      const bg = CARRIER_BG[carrier];
                      if (!cell) {
                        const isEditNew = editCell?.model === row.model && editCell?.carrier === carrier && editCell?.field === 'newmargin';
                        return (
                          <td key={carrier} colSpan={3} className={`px-2 py-2 text-center ${bg}`}>
                            {isEditNew ? (
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-xs text-muted-foreground">$</span>
                                <Input
                                  type="number" step="1" value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  onBlur={commitEdit}
                                  onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditCell(null); }}
                                  autoFocus placeholder="margin"
                                  className="h-7 text-right w-20"
                                  data-testid={`newmargin-input-${row.model}-${carrier}`}
                                />
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit(row.model, carrier, 'newmargin', '')}
                                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground rounded px-2 py-1 hover:bg-white/60 dark:hover:bg-black/30 transition-colors"
                                data-testid={`set-margin-${row.model}-${carrier}`}
                                title={`Set your ${carrier} profit margin for ${row.model}`}
                              >
                                <Plus className="h-3 w-3" /> Set margin
                              </button>
                            )}
                          </td>
                        );
                      }
                      const isEditMargin = editCell?.model === row.model && editCell?.carrier === carrier && editCell?.field === 'margin';
                      const isEditBuyer = editCell?.model === row.model && editCell?.carrier === carrier && editCell?.field === 'manual_buyer';
                      const isICloud = carrier === 'iCloud Locked';
                      // For working carriers, use grade_breakdown[workingGrade]. For iCloud, use grade_breakdown[icloudGrade].
                      const breakdown = cell.grade_breakdown || {};
                      const gradeKey = isICloud ? icloudGrade : workingGrade;
                      const gv = breakdown[gradeKey] || {};
                      const displayBuyer = gv.buyer_price;
                      const displayPay = gv.paying_price;
                      const displayMatchedStorage = gv.matched_storage;
                      // Manual override only applies to Grade A (working) and A/B (iCloud)
                      const isManualEligible = section === 'used' && !!row.storage && ((isICloud && icloudGrade === 'A/B') || (!isICloud && workingGrade === 'A'));
                      const buyerIsManual = isManualEligible && cell.source === 'manual' && displayBuyer === cell.buyer_price;
                      return (
                        <Fragment key={`${row.model}-${carrier}`}>
                          <td className={`px-2 py-2 text-right ${bg}`}>
                            {isEditBuyer ? (
                              <Input
                                type="number" step="1" value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') commitEdit();
                                  if (e.key === 'Escape') setEditCell(null);
                                }}
                                autoFocus
                                className="h-7 text-right w-20 ml-auto"
                                data-testid={`buyer-input-${row.model}-${carrier}`}
                              />
                            ) : (
                              <button
                                onClick={() => isManualEligible && startEdit(row.model, carrier, 'manual_buyer', cell.source === 'manual' ? cell.buyer_price : '')}
                                disabled={!isManualEligible}
                                className={`px-1.5 py-0.5 rounded transition-colors text-right w-full ${isManualEligible ? 'hover:bg-white/60 dark:hover:bg-black/30' : 'cursor-default'}`}
                                data-testid={`buyer-cell-${row.model}-${carrier}`}
                                title={
                                  displayBuyer == null
                                    ? `No K3no Grade ${gradeKey} price found${isManualEligible ? ' · click to set manual buyer price' : ''}`
                                    : buyerIsManual
                                      ? 'Manually set buyer price · click to edit'
                                      : `From K3no (Grade ${gradeKey}, ${displayMatchedStorage || 'any'})`
                                }
                              >
                                {displayBuyer != null ? (
                                  <span className="inline-flex items-baseline gap-1 justify-end">
                                    <span className={buyerIsManual ? 'italic text-muted-foreground' : 'text-foreground/80'}>
                                      ${displayBuyer.toLocaleString()}
                                    </span>
                                    {!isICloud && displayMatchedStorage && displayMatchedStorage !== 'ANY' && (
                                      <span className="text-[9px] leading-none text-muted-foreground/60 font-normal">{displayMatchedStorage}</span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground/60 text-xs">—</span>
                                )}
                              </button>
                            )}
                          </td>
                          <td className={`px-2 py-2 text-right ${bg}`}>
                            {isEditMargin ? (
                              <MarginEditor testId={`margin-input-${row.model}-${carrier}`} value={editValue} onChange={setEditValue}
                                mode={editMode} onMode={setEditMode} allowPercentage={section !== 'used'}
                                onSave={commitEdit} onCancel={() => setEditCell(null)} />
                            ) : (
                              <button
                                onClick={() => startEdit(row.model, carrier, 'margin', cell.margin)}
                                className="px-1.5 py-0.5 rounded hover:bg-white/60 dark:hover:bg-black/30 transition-colors text-right w-full text-muted-foreground"
                                data-testid={`margin-cell-${row.model}-${carrier}`}
                              >
                                {marginLabel(cell)}
                                <Pencil className="h-2.5 w-2.5 inline ml-1 opacity-40" />
                              </button>
                            )}
                          </td>
                          <td className={`px-2 py-2 text-right font-bold ${bg} ${CARRIER_COLOR[carrier]}`} data-testid={`pay-${row.model}-${carrier}`}>
                            <button
                              onClick={() => setDrill({ model: row.model, carrier })}
                              className="px-1.5 py-0.5 rounded hover:bg-white/60 dark:hover:bg-black/30 transition-colors w-full"
                              title="Click to see all grades"
                              data-testid={`pay-btn-${row.model}-${carrier}`}
                            >
                              {displayPay != null ? (
                                <span className="inline-flex items-baseline gap-1 justify-center">
                                  <span>${displayPay.toLocaleString()}</span>
                                  {!isICloud && displayMatchedStorage && displayMatchedStorage !== 'ANY' && (
                                    <span className="text-[9px] leading-none opacity-50 font-normal">{displayMatchedStorage}</span>
                                  )}
                                </span>
                              ) : '—'}
                            </button>
                            <p className="text-[11px] font-medium text-foreground/70" data-testid={`profit-${row.model}-${carrier}`}>
                              Profit {displayBuyer != null && displayPay != null ? `$${(displayBuyer - displayPay).toLocaleString(undefined, {maximumFractionDigits: 2})}` : '—'}
                            </p>
                          </td>
                        </Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list — replaces the wide table on phones so prices are readable */}
          <div className="sm:hidden divide-y divide-border" data-testid="mobile-price-list">
            {filteredRows.map(row => (
              <div key={`m-${row.model}`} className="px-4 py-4" data-testid={`m-row-${row.model}`}>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="font-semibold text-foreground text-base">{row.model}</span>
                  {row.available_storages?.length > 0 && (
                    <Select value={row.storage || ''} onValueChange={(v) => setModelStorage(row.model, v)}>
                      <SelectTrigger
                        className="h-7 w-[96px] text-xs px-2 py-0 font-normal text-muted-foreground shrink-0"
                        data-testid={`m-storage-select-${row.model}`}
                      >
                        <SelectValue placeholder="Storage?" />
                      </SelectTrigger>
                      <SelectContent>
                        {row.available_storages.map((s) => (
                          <SelectItem key={s} value={s} className="text-xs" data-testid={`m-storage-opt-${row.model}-${s}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  {displayedCarriers.map(carrier => {
                    const cell = row.carriers?.[carrier];
                    const bg = CARRIER_BG[carrier];
                    const Icon = CARRIER_ICON[carrier];
                    if (!cell) {
                      const isEditNew = editCell?.model === row.model && editCell?.carrier === carrier && editCell?.field === 'newmargin';
                      return (
                        <div key={`m-${row.model}-${carrier}`} className={`rounded-lg px-3 py-2.5 flex items-center justify-between ${bg}`}>
                          <span className={`flex items-center gap-1.5 text-sm font-semibold ${CARRIER_COLOR[carrier]}`}><Icon className="h-4 w-4" />{carrier}</span>
                          {isEditNew ? (
                            <Input
                              type="number" step="1" value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditCell(null); }}
                              autoFocus placeholder="margin" className="h-7 w-20 text-xs px-1.5 text-right"
                              data-testid={`m-newmargin-input-${row.model}-${carrier}`}
                            />
                          ) : (
                            <button
                              onClick={() => startEdit(row.model, carrier, 'newmargin', '')}
                              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                              data-testid={`m-set-margin-${row.model}-${carrier}`}
                            >
                              <Plus className="h-3 w-3" /> Set margin
                            </button>
                          )}
                        </div>
                      );
                    }
                    const isICloud = carrier === 'iCloud Locked';
                    const breakdown = cell.grade_breakdown || {};
                    const gradeKey = isICloud ? icloudGrade : workingGrade;
                    const gv = breakdown[gradeKey] || {};
                    const displayBuyer = gv.buyer_price;
                    const displayPay = gv.paying_price;
                    const displayMatchedStorage = gv.matched_storage;
                    const isManualEligible = section === 'used' && !!row.storage && ((isICloud && icloudGrade === 'A/B') || (!isICloud && workingGrade === 'A'));
                    const isEditMargin = editCell?.model === row.model && editCell?.carrier === carrier && editCell?.field === 'margin';
                    const isEditBuyer = editCell?.model === row.model && editCell?.carrier === carrier && editCell?.field === 'manual_buyer';
                    return (
                      <div key={`m-${row.model}-${carrier}`} className={`rounded-lg px-3 py-2.5 ${bg}`} data-testid={`m-cell-${row.model}-${carrier}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className={`flex items-center gap-1.5 text-sm font-semibold ${CARRIER_COLOR[carrier]}`}><Icon className="h-4 w-4" />{carrier}</span>
                          <button
                            onClick={() => setDrill({ model: row.model, carrier })}
                            className={`text-xl font-bold leading-none ${CARRIER_COLOR[carrier]}`}
                            title="Tap to see all grades"
                            data-testid={`m-pay-${row.model}-${carrier}`}
                          >
                            {displayPay != null ? (
                              <span className="inline-flex items-baseline gap-1">
                                <span>${displayPay.toLocaleString()}</span>
                                {!isICloud && displayMatchedStorage && displayMatchedStorage !== 'ANY' && (
                                  <span className="text-[9px] leading-none opacity-50 font-normal">{displayMatchedStorage}</span>
                                )}
                              </span>
                            ) : '—'}
                          </button>
                        </div>
                        <p className="mt-1 text-xs font-medium text-foreground/70" data-testid={`m-profit-${row.model}-${carrier}`}>
                          Profit {displayBuyer != null && displayPay != null ? `$${(displayBuyer - displayPay).toLocaleString(undefined, {maximumFractionDigits: 2})}` : '—'}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <span className="uppercase tracking-wide text-[10px] text-muted-foreground/70">K3no</span>
                            {isEditBuyer ? (
                              <Input
                                type="number" step="1" value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditCell(null); }}
                                autoFocus className="h-6 w-16 text-xs px-1.5"
                                data-testid={`m-buyer-input-${row.model}-${carrier}`}
                              />
                            ) : (
                              <button
                                onClick={() => isManualEligible && startEdit(row.model, carrier, 'manual_buyer', cell.source === 'manual' ? cell.buyer_price : '')}
                                disabled={!isManualEligible}
                                className={`text-foreground/80 ${isManualEligible ? 'underline decoration-dotted underline-offset-2' : ''}`}
                                data-testid={`m-buyer-${row.model}-${carrier}`}
                              >
                                {displayBuyer != null ? `$${displayBuyer.toLocaleString()}` : '—'}
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="uppercase tracking-wide text-[10px] text-muted-foreground/70">Profit target</span>
                            {isEditMargin ? (
                              <MarginEditor testId={`m-margin-input-${row.model}-${carrier}`} value={editValue} onChange={setEditValue}
                                mode={editMode} onMode={setEditMode} allowPercentage={section !== 'used'}
                                onSave={commitEdit} onCancel={() => setEditCell(null)} />
                            ) : (
                              <button
                                onClick={() => startEdit(row.model, carrier, 'margin', cell.margin)}
                                className="text-foreground/80 underline decoration-dotted underline-offset-2 inline-flex items-center gap-1"
                                data-testid={`m-margin-${row.model}-${carrier}`}
                              >
                                {marginLabel(cell)} <Pencil className="h-2.5 w-2.5 opacity-40" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 py-2 text-xs text-muted-foreground bg-muted/30 border-t border-border">
            Tip: Click any K3no or Margin cell to edit. Click a <span className="font-semibold">Pay</span> cell to see all grades. iCloud Locked pulls prices from K3no's <span className="font-semibold">Parts/IC</span> sheet (grades A/B, C, D, DOA).
          </div>
        </Card>
      )}

      <Dialog open={addPriceOpen} onOpenChange={setAddPriceOpen}>
        <DialogContent data-testid="add-device-price-dialog">
          <DialogHeader>
            <DialogTitle>Add device price</DialogTitle>
            <DialogDescription>Add iPhones, MacBooks, iPads or any model. Sealed and Opened prices are separate from used grades. Existing sheet sync continues; editing a synced price may be overwritten by the next sync.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <label className="col-span-2 text-sm space-y-1">Model name
              <Input data-testid="device-price-model" placeholder="e.g. MacBook Pro M4 16GB RAM" value={newPrice.model} onChange={e => setNewPrice(p => ({ ...p, model: e.target.value }))} />
            </label>
            <label className="text-sm space-y-1">Storage
              <Input data-testid="device-price-storage" placeholder="512GB, 1TB or ANY" value={newPrice.storage} onChange={e => setNewPrice(p => ({ ...p, storage: e.target.value }))} />
            </label>
            <div className="text-sm space-y-1">Condition
              <Select value={newPrice.grade} onValueChange={grade => setNewPrice(p => ({ ...p, grade }))}>
                <SelectTrigger data-testid="device-price-grade"><SelectValue /></SelectTrigger>
                <SelectContent>{WORKING_GRADES.map(g => <SelectItem data-testid={`device-price-grade-${g}`} key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 text-sm space-y-1">Carrier / connectivity
              <Select value={newPrice.carrier} onValueChange={carrier => setNewPrice(p => ({ ...p, carrier }))}>
                <SelectTrigger data-testid="device-price-carrier"><SelectValue /></SelectTrigger>
                <SelectContent>{CARRIERS.filter(c => c !== 'iCloud Locked').map(c => <SelectItem data-testid={`device-price-carrier-${c}`} key={c} value={c}>{c === 'Unlocked' ? 'Unlocked / Wi-Fi / No carrier' : c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="text-sm space-y-1">Buyer price ($)
              <Input data-testid="device-price-amount" type="number" min="0" step="0.01" value={newPrice.price} onChange={e => setNewPrice(p => ({ ...p, price: e.target.value }))} />
            </label>
            <label className="text-sm space-y-1">Your profit margin ($)
              <Input data-testid="device-price-margin" type="number" min="0" step="0.01" value={newPrice.profit_margin} onChange={e => setNewPrice(p => ({ ...p, profit_margin: e.target.value }))} />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="device-price-cancel" onClick={() => setAddPriceOpen(false)}>Cancel</Button>
            <Button data-testid="device-price-save" disabled={savingPrice} onClick={addPrice}>{savingPrice ? 'Saving…' : 'Save price'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync dialog */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent data-testid="sync-dialog">
          <DialogHeader>
            <DialogTitle>K3no Google Sheet Auto-Sync</DialogTitle>
            <DialogDescription>
              Paste K3no's buyer-price Google Sheet link once. It auto-syncs every hour and
              recalculates your paying prices — you don't need to keep re-pasting the URL.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">
                K3no "Used iPhones" Sheet URL
              </label>
              <Input
                placeholder="https://docs.google.com/spreadsheets/d/.../edit#gid=0"
                value={syncCfg.sheet_url || ''}
                onChange={e => setSyncCfg(s => ({ ...s, sheet_url: e.target.value }))}
                data-testid="sync-url-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Powers all five device sections. New Phones, New iPads, Used iPads and Apple Watch are found automatically by tab name.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">
                K3no "Parts / IC" Sheet URL
              </label>
              <Input
                placeholder="https://docs.google.com/spreadsheets/d/.../edit#gid=..."
                value={syncCfg.sheet_url_parts_ic || ''}
                onChange={e => setSyncCfg(s => ({ ...s, sheet_url_parts_ic: e.target.value }))}
                data-testid="sync-url-parts-ic-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Powers iCloud Locked paying prices (grades A/B, C, D, DOA).
              </p>
            </div>
            <p className="text-xs text-muted-foreground border-l-2 border-blue-500 pl-3 py-1">
              Each sheet must be shared as "Anyone with the link can view" (File → Share → General access).
              Auto-syncs hourly. Click <span className="font-semibold">Sync Now</span> to refresh all configured sections immediately.
            </p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400 border-l-2 border-emerald-500 pl-3 py-1">
              Set it once, then forget it. Paste the normal <span className="font-mono">/edit</span> share link for the
              <span className="font-semibold"> Used iPhones</span> tab in the first box and the
              <span className="font-semibold"> Parts/IC</span> tab in the second. FlipTrack now follows each tab by name,
              so prices keep syncing even if K3no recreates a tab and its link changes — no more re-pasting.
            </p>
            <p className="text-xs text-muted-foreground border-l-2 border-amber-500 pl-3 py-1">
              Only requirement: the buyer's sheet stays shared as &ldquo;Anyone with the link&rdquo;. If it's ever
              restricted to their organisation, use <span className="font-semibold">Import MM_PHONEZ</span> (download each tab as CSV) instead.
            </p>
            {syncCfg.last_synced_at && (
              <div className="text-xs text-muted-foreground border-l-2 border-emerald-500 pl-3 py-1">
                Last synced {fmtSince(syncCfg.last_synced_at)}.
                {syncCfg.last_sync_result && (
                  <> Imported {syncCfg.last_sync_result.imported}, updated {syncCfg.last_sync_result.updated}.</>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncOpen(false)}>Close</Button>
            <Button variant="outline" onClick={handleSyncSave} data-testid="save-sync-url-btn">
              <Save className="h-4 w-4 mr-2" /> Save URL
            </Button>
            <Button onClick={handleSyncNow} disabled={syncing || (!syncCfg.sheet_url && !syncCfg.sheet_url_parts_ic)} data-testid="sync-now-dialog-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing…' : 'Sync Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-grade drill-down */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent data-testid="drill-dialog">
          {drill && (() => {
            const row = rows.find(r => r.model === drill.model);
            const cell = row?.carriers?.[drill.carrier];
            if (!cell) return null;
            const isICloud = drill.carrier === 'iCloud Locked';
            const grades = isICloud ? PARTS_IC_GRADES : sectionGrades;
            const Icon = CARRIER_ICON[drill.carrier];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Icon className={`h-5 w-5 ${CARRIER_COLOR[drill.carrier]}`} />
                    {drill.model} · {drill.carrier}
                  </DialogTitle>
                  <DialogDescription>
                    Your profit margin: <span className="font-semibold">{marginLabel(cell)}</span>
                    {cell.storage && cell.storage !== 'ANY' && <> · Storage: {cell.storage}</>}
                  </DialogDescription>
                </DialogHeader>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground text-xs uppercase">
                      <th className="text-left py-2">Grade</th>
                      <th className="text-right py-2">K3no Buyer</th>
                      <th className="text-right py-2">Your Pay</th>
                      <th className="text-right py-2">Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grades.map(g => {
                      const gv = cell.grade_breakdown?.[g] || {};
                      return (
                        <tr key={g} className="border-b border-border/50 hover:bg-muted/30" data-testid={`drill-row-${g.replace(/[^A-Za-z0-9]/g, '')}`}>
                          <td className="py-2.5 font-semibold text-foreground">Grade {g}</td>
                          <td className="py-2.5 text-right text-foreground/80">
                            {gv.buyer_price != null ? `$${gv.buyer_price.toLocaleString()}` : <span className="text-muted-foreground/50">—</span>}
                          </td>
                          <td className={`py-2.5 text-right font-bold ${gv.paying_price != null && gv.paying_price < 0 ? 'text-red-600' : CARRIER_COLOR[drill.carrier]}`}>
                            {gv.paying_price != null ? `$${gv.paying_price.toLocaleString()}` : <span className="text-muted-foreground/50">—</span>}
                          </td>
                          <td className="py-2.5 text-right" data-testid={`drill-profit-${g.replace(/[^A-Za-z0-9]/g, '')}`}>
                            {gv.buyer_price != null && gv.paying_price != null ? `$${(gv.buyer_price - gv.paying_price).toLocaleString(undefined, {maximumFractionDigits: 2})}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-xs text-muted-foreground mt-2">
                  {section === 'used' ? 'Pay = K3no Buyer Price − Your Margin.' : 'Pay = K3no Buyer Price minus your $ profit or % margin, rounded DOWN to $10. Profit below includes that rounding.'}
                </p>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Import result / errors dialog */}
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.imported === 0 && importResult?.updated === 0 ? (
                <><X className="h-5 w-5 text-red-600" /> Couldn't read your CSV</>
              ) : (
                <>Import details</>
              )}
            </DialogTitle>
            <DialogDescription>
              {importResult?.file && <>File: <span className="font-mono">{importResult.file}</span></>}
            </DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-50 dark:bg-emerald-950/30 p-3 rounded">
                  <div className="text-2xl font-display text-emerald-700">{importResult.imported}</div>
                  <div className="text-xs text-emerald-700/80 uppercase">New</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded">
                  <div className="text-2xl font-display text-blue-700">{importResult.updated}</div>
                  <div className="text-xs text-blue-700/80 uppercase">Updated</div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded">
                  <div className="text-2xl font-display text-amber-700">{importResult.total_errors || 0}</div>
                  <div className="text-xs text-amber-700/80 uppercase">Skipped</div>
                </div>
              </div>
              {importResult.imported === 0 && importResult.updated === 0 && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded p-3 text-xs text-red-800 dark:text-red-200">
                  <p className="font-semibold mb-1">No rows could be parsed. Common fixes:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>Open the Google Sheet → <span className="font-mono">File → Download → CSV</span></li>
                    <li>Make sure each phone block starts with a model header row (e.g., <span className="font-mono">iPhone 17 Pro Max</span>)</li>
                    <li>Each carrier row needs the keywords <span className="font-mono">Unlocked</span>, <span className="font-mono">carrier locked</span>, or <span className="font-mono">passcode/IC</span></li>
                    <li>Profit cell needs the text <span className="font-mono">Profit = $...</span></li>
                  </ul>
                </div>
              )}
              {importResult.errors?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <p className="font-semibold mb-1">Skipped rows (first 10):</p>
                  <ul className="list-disc ml-4 space-y-0.5">
                    {importResult.errors.map((e, i) => <li key={i} className="font-mono">{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportResult(null)} data-testid="import-result-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
